/**
 * TestChat — вкладка «Тестировать бота»: переписка с агентом клиента прямо в кабинете.
 *
 * Зачем: до этого проверить бота можно было только через Telegram — надо было
 * иметь второй аккаунт, нажимать Start и вручную играть покупателя. Здесь тот же
 * движок и та же база знаний, но в изоляции: живые диалоги, память и передачи
 * оператору не трогаются.
 *
 * Диалог многоходовый: историю держит эта вкладка и присылает целиком на каждый
 * ход, поэтому бот помнит предыдущие реплики. На сервере тестовая переписка не
 * сохраняется: закрыл вкладку — её нет.
 *
 * ВРЕМЯ ОЖИДАНИЯ (06.08) — инструмент тестирования, а не настройка прода: у Тони
 * понятия задержки нет, ход считается по каждому сообщению. Здесь окно ожидания
 * ИМИТИРУЕТ живого клиента, который пишет мысль в три сообщения подряд: всё, что
 * отправлено внутри окна, склеивается и уходит боту ОДНИМ ходом. Так проверяется
 * поведение на рваном вводе, которое иначе видно только в реальном мессенджере.
 */

import { useEffect, useRef, useState } from 'react'
import { Clock, Loader2, RotateCcw, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { testChatApi } from '@/api/client'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

interface Msg {
  role: 'user' | 'bot' | 'error' | 'system'
  text: string
}

type Turn = { role: 'user' | 'assistant'; content: string }

/**
 * Шкала окна ожидания: от 5 секунд с шагом 5 (решение основателя, 06.08).
 * Меньше пяти смысла не имеет — клиент не успевает дописать вторую мысль, окно
 * закрывается, и получается прежний ответ на каждое сообщение, только медленнее.
 * `null` — отправлять сразу, без окна.
 */
const MIN_DELAY = 5
const DELAY_PRESETS: { label: string; value: number | null }[] = [
  { label: 'Не задано', value: null },
  ...[5, 10, 15, 20, 25, 30, 35, 40].map((v) => ({ label: `${v} сек`, value: v })),
]

/** Подсказки-заготовки: то, что чаще всего проверяют после правки БЗ. */
const SUGGESTIONS = [
  'Сколько стоит?',
  'Чем вы занимаетесь?',
  'Хочу записаться на замер',
  'Позовите человека',
]

export function TestChat({ dealId }: { dealId: string }) {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  // Источник истины для истории: только завершённые ходы. Отдельно от messages,
  // потому что в ленте есть служебные пометки и ещё не отправленный буфер.
  const historyRef = useRef<Turn[]>([])

  // ---- окно ожидания ----
  const [delaySec, setDelaySec] = useState<number | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [customDelay, setCustomDelay] = useState('')
  const [countdown, setCountdown] = useState<number | null>(null)
  const bufferRef = useRef<string[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (tickRef.current) clearInterval(tickRef.current)
  }, [])

  function scrollDown() {
    requestAnimationFrame(() => {
      const el = listRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }

  /** Отправить накопленный буфер одним ходом. */
  async function flush() {
    const text = bufferRef.current.join('\n').trim()
    bufferRef.current = []
    setCountdown(null)
    if (tickRef.current) clearInterval(tickRef.current)
    timerRef.current = null
    if (!text) return

    setSending(true)
    try {
      if (USE_MOCK) {
        setMessages((m) => [...m, { role: 'bot', text: 'Демо-режим: ответ не запрашивается.' }])
        return
      }
      const res = await testChatApi.ask(dealId, text, historyRef.current)
      historyRef.current = [
        ...historyRef.current,
        { role: 'user', content: text },
        { role: 'assistant', content: res.answer || '' },
      ]
      setMessages((m) => [
        ...m,
        { role: 'bot', text: res.answer || '(пустой ответ)' },
        ...(res.handoff
          ? [{ role: 'system' as const, text: 'Бот передал диалог оператору (в тесте передача не отправляется).' }]
          : []),
      ])
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'error', text: e instanceof Error ? e.message : 'Не удалось получить ответ.' },
      ])
    } finally {
      setSending(false)
      scrollDown()
    }
  }

  function send(text: string) {
    const msg = text.trim()
    if (!msg || sending) return
    setInput('')
    setMessages((m) => [...m, { role: 'user', text: msg }])
    bufferRef.current.push(msg)
    scrollDown()

    // Без задержки — уходит сразу.
    if (delaySec === null) {
      void flush()
      return
    }

    // Окно уже открыто: сообщение просто присоединяется к текущему ходу,
    // таймер НЕ перезапускаем — иначе непрерывный ввод откладывал бы ответ вечно.
    if (timerRef.current) return

    setCountdown(delaySec)
    tickRef.current = setInterval(() => {
      setCountdown((c) => (c === null || c <= 1 ? null : c - 1))
    }, 1000)
    timerRef.current = setTimeout(() => void flush(), delaySec * 1000)
  }

  function reset() {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (tickRef.current) clearInterval(tickRef.current)
    timerRef.current = null
    bufferRef.current = []
    historyRef.current = []
    setCountdown(null)
    setMessages([])
  }

  const delayLabel = delaySec === null ? 'Не задано' : `${delaySec} сек`

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">
          Тот же движок и та же база знаний, что у живого бота. Диалоги клиентов
          и передачи оператору не затрагиваются.
        </p>

        <div className="flex items-center gap-2">
          {/* Меню окна ожидания */}
          <div className="relative">
            <button
              type="button"
              title="Время ожидания в тестовом чате"
              onClick={() => setMenuOpen((v) => !v)}
              className={`flex h-7 items-center gap-1.5 rounded border px-2 text-xs ${
                delaySec === null ? 'text-muted-foreground' : 'bg-accent font-medium'
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              {delayLabel}
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border bg-background p-2 shadow-lg">
                  <p className="px-2 pb-2 text-xs font-medium">Время ожидания в тестовом чате</p>
                  {DELAY_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        setDelaySec(p.value)
                        setMenuOpen(false)
                      }}
                      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <span className={p.value === null ? 'font-medium' : ''}>{p.label}</span>
                      {delaySec === p.value && <span>✓</span>}
                    </button>
                  ))}
                  <div className="mt-2 flex items-center gap-2 border-t pt-2">
                    <span className="px-2 text-sm">Любое</span>
                    <input
                      type="number"
                      min={MIN_DELAY}
                      max={600}
                      value={customDelay}
                      placeholder="—"
                      onChange={(e) => setCustomDelay(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        const n = parseInt(customDelay, 10)
                        if (!isNaN(n) && n >= MIN_DELAY && n <= 600) {
                          setDelaySec(n)
                          setMenuOpen(false)
                        }
                      }}
                      className="w-16 rounded border px-2 py-1 text-sm text-center"
                    />
                    <span className="text-xs text-muted-foreground">сек</span>
                  </div>
                  <p className="px-2 pt-2 text-[11px] text-muted-foreground">
                    Минимум 5 секунд. Всё, что отправлено внутри окна, уходит боту
                    одним ходом — как когда клиент пишет мысль в несколько сообщений.
                  </p>
                </div>
              </>
            )}
          </div>

          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs h-7"
            disabled={messages.length === 0 || sending}
            onClick={reset}
          >
            <RotateCcw className="h-3 w-3" />
            Очистить
          </Button>
        </div>
      </div>

      <div
        ref={listRef}
        className="h-[360px] overflow-y-auto rounded-md border bg-slate-50/60 p-3 space-y-2"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">
              Напишите боту как клиент — он ответит по текущей базе знаний.
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  className="rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground hover:bg-accent"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-slate-900 text-white'
                    : m.role === 'error'
                      ? 'bg-amber-50 border border-amber-200 text-amber-900'
                      : m.role === 'system'
                        ? 'bg-blue-50 border border-blue-200 text-blue-900 text-xs'
                        : 'bg-background border'
                }`}
              >
                {m.text}
              </div>
            </div>
          ))
        )}

        {countdown !== null && (
          <div className="flex justify-end">
            <div className="rounded-lg border border-dashed px-3 py-1.5 text-xs text-muted-foreground">
              Жду ещё {countdown} с — можно дописать, уйдёт одним сообщением
            </div>
          </div>
        )}

        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Бот печатает…
            </div>
          </div>
        )}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          send(input)
        }}
      >
        <input
          className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Сообщение…"
          value={input}
          disabled={sending}
          onChange={(e) => setInput(e.target.value)}
        />
        <Button type="submit" size="sm" className="gap-1.5" disabled={sending || !input.trim()}>
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Отправить
        </Button>
      </form>

      <p className="text-[11px] text-muted-foreground">
        Бот помнит переписку внутри этой вкладки — можно вести многоходовой диалог.
        «Очистить» начинает разговор с нуля. На сервере тестовая переписка не
        сохраняется. Каждый ответ расходует лимит LLM.
      </p>
    </div>
  )
}
